import { supabase } from '../supabase';

/**
 * Configuration for auto-sync toggles
 */
export interface AutoSyncConfig {
  products: boolean;
  price_lists: boolean;
  categories: boolean;
  stock: boolean;
}

export type SyncItemType = 'product' | 'category' | 'price' | 'stock';
export type SyncAction = 'create' | 'update' | 'delete';

interface SyncOptions {
  stockChange?: number;
  reason?: string;
  sku?: string;
  variationId?: string;
}

type ToastType = 'success' | 'error' | 'info';
type ToastCallback = (message: string, type: ToastType) => void;

/**
 * Service for managing automatic WooCommerce synchronization
 * Provides toggle-based control for syncing products, categories, prices, and stock
 */
class AutoSyncService {
  private config: AutoSyncConfig | null = null;
  private organizationId: string | null = null;
  private toastCallback: ToastCallback | null = null;
  private initialized = false;

  /**
   * Initialize the service with organization ID
   */
  async initialize(organizationId: string): Promise<void> {
    this.organizationId = organizationId;
    await this.loadConfig();
    this.initialized = true;
    console.log('[AutoSync] Initialized for organization:', organizationId);
  }

  /**
   * Set callback function for toast notifications
   */
  setToastCallback(callback: ToastCallback): void {
    this.toastCallback = callback;
  }

  /**
   * Load auto-sync configuration from database
   */
  async loadConfig(): Promise<void> {
    if (!this.organizationId) return;

    try {
      const { data } = await supabase
        .from('api_configurations')
        .select('configuration')
        .eq('organization_id', this.organizationId)
        .eq('service_name', 'woocommerce')
        .eq('is_active', true)
        .maybeSingle();

      this.config = data?.configuration?.auto_sync || {
        products: false,
        price_lists: false,
        categories: false,
        stock: false
      };

      console.log('[AutoSync] Config loaded:', this.config);
    } catch (error) {
      console.error('[AutoSync] Error loading config:', error);
      this.config = {
        products: false,
        price_lists: false,
        categories: false,
        stock: false
      };
    }
  }

  /**
   * Update auto-sync configuration in database
   */
  async updateConfig(config: Partial<AutoSyncConfig>): Promise<void> {
    if (!this.organizationId) throw new Error('AutoSyncService not initialized');

    try {
      // Get current configuration
      const { data: currentData } = await supabase
        .from('api_configurations')
        .select('configuration')
        .eq('organization_id', this.organizationId)
        .eq('service_name', 'woocommerce')
        .single();

      if (!currentData) {
        throw new Error('WooCommerce configuration not found');
      }

      // Merge auto_sync config
      const newConfiguration = {
        ...currentData.configuration,
        auto_sync: {
          ...currentData.configuration?.auto_sync,
          ...config
        }
      };

      // Update in database
      const { error } = await supabase
        .from('api_configurations')
        .update({ configuration: newConfiguration })
        .eq('organization_id', this.organizationId)
        .eq('service_name', 'woocommerce');

      if (error) throw error;

      // Update local config
      this.config = newConfiguration.auto_sync;
      console.log('[AutoSync] Config updated:', this.config);
    } catch (error) {
      console.error('[AutoSync] Error updating config:', error);
      throw error;
    }
  }

  /**
   * Check if auto-sync is enabled for a specific type
   */
  isEnabled(type: SyncItemType): boolean {
    if (!this.config || !this.initialized) return false;

    const mapping: Record<SyncItemType, keyof AutoSyncConfig> = {
      'product': 'products',
      'category': 'categories',
      'price': 'price_lists',
      'stock': 'stock'
    };

    return this.config[mapping[type]] || false;
  }

  /**
   * Trigger synchronization for a single item
   * Only executes if auto-sync is enabled for the item type
   */
  async triggerSync(
    type: SyncItemType,
    itemId: string,
    action: SyncAction,
    options?: SyncOptions,
    force?: boolean
  ): Promise<boolean> {
    // Check if auto-sync is enabled for this type (skip check if force=true)
    if (!force && !this.isEnabled(type)) {
      console.log(`[AutoSync] Skipped: ${type} sync is disabled`);
      return false;
    }

    // Get type label for messages
    const typeLabels: Record<SyncItemType, string> = {
      'product': 'prodotto',
      'category': 'categoria',
      'price': 'prezzo',
      'stock': 'stock'
    };

    try {
      this.toastCallback?.(`Sincronizzazione ${typeLabels[type]} in corso...`, 'info');

      const { data: session } = await supabase.auth.getSession();

      if (!session?.session?.access_token) {
        console.error('[AutoSync] No session found');
        this.toastCallback?.('Errore: sessione non valida', 'error');
        return false;
      }

      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;

      const response = await fetch(
        `${supabaseUrl}/functions/v1/sync-single-item`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${session.session.access_token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            item_type: type,
            item_id: itemId,
            action: action,
            stock_change: options?.stockChange,
            reason: options?.reason,
            sku: options?.sku,
            variation_id: options?.variationId
          })
        }
      );

      const result = await response.json();

      if (result.success) {
        const actionLabels: Record<SyncAction, string> = {
          'create': 'creato',
          'update': 'aggiornato',
          'delete': 'eliminato'
        };
        this.toastCallback?.(
          `${typeLabels[type].charAt(0).toUpperCase() + typeLabels[type].slice(1)} ${actionLabels[action]} su WooCommerce`,
          'success'
        );
        console.log(`[AutoSync] Success: ${type} ${action}`, result);
        return true;
      } else {
        console.error('[AutoSync] Error:', result.error || result.message);
        // Don't show error toast if sync is just disabled
        if (result.message?.includes('disabled')) {
          console.log('[AutoSync] Sync disabled for this type');
        } else {
          this.toastCallback?.(`Errore sync: ${result.error || result.message}`, 'error');
        }
        return false;
      }
    } catch (error) {
      console.error('[AutoSync] Exception:', error);
      this.toastCallback?.('Errore nella sincronizzazione', 'error');
      return false;
    }
  }

  /**
   * Get current configuration
   */
  getConfig(): AutoSyncConfig | null {
    return this.config;
  }

  /**
   * Check if service is initialized
   */
  isInitialized(): boolean {
    return this.initialized;
  }

  /**
   * Reset service (for logout)
   */
  reset(): void {
    this.config = null;
    this.organizationId = null;
    this.initialized = false;
    console.log('[AutoSync] Service reset');
  }
}

// Export singleton instance
export const autoSyncService = new AutoSyncService();
